import * as React from 'react';
import { LineInterface, LineOrder } from '../../Interfaces/Draft/LineInterface';
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface';
import { l } from '../../Other/Translation';
import { convertVatpctFromLocale } from '../../Other/functions/vatFunctions';
import { NumberInput } from '../common/NumberInput';
import { SelectInput } from '../common/SelectInput/SelectInput';
import { Input } from '../common/Input/Input';
import { NumberFormatter } from '../../Other/Format/NumberFormatter';
import { Format } from '../../Other/Format/Format';

export interface InvoiceLinePropsInterface extends LineInterface {
  onDelete?: (e: Event) => void;
  onChange?: (values: LineInterface) => void;
  onDrop?: (which: number) => void;
  automation?: string;
  columns?: Array<keyof LineInterface>;
  currencyFormat?: NumberFormatter;
  settings?: SettingsInterface;
  id?: string;
  index: number;
  locale?: string;
  readOnly?: boolean;
}

export class InvoiceLine extends React.Component<InvoiceLinePropsInterface, LineInterface> {
  private currencyF: NumberFormatter;
  private numberF: NumberFormatter;

  constructor(props: InvoiceLinePropsInterface) {
    super(props);

    this.currencyF = this.props.currencyFormat;
    this.numberF = new NumberFormatter(Format.fromLocale(props.locale));

    this.state = this.propsToState(props);
  }

  private propsToState(props: InvoiceLinePropsInterface): LineInterface {
    return {
      type: props.type,
      grossTotal: props.grossTotal,
      quantity: props.quantity,
      description: props.description,
      unitPrice: props.unitPrice,
      netTotal: props.netTotal,
      taxTotal: props.taxTotal,
      vatPct: props.vatPct,
      unit: props.unit,
      focused: props.focused,
      dragOn: false
    };
  }

  public static defaultProps: Partial<InvoiceLinePropsInterface> = {
    description: '',
    grossTotal: 0,
    quantity: 0,
    unit: '',
    unitPrice: 0,
    vatPct: 0,
    netTotal: 0,
    taxTotal: 0,
    type: 'row',
    focused: false,
    locale: 'en_GB',
    readOnly: false,
  };

  public componentWillReceiveProps(props: InvoiceLinePropsInterface) {
    this.setState(this.propsToState(props));
    if (props.currencyFormat) {
      this.currencyF = props.currencyFormat;
    }
  }

  private canDrag: boolean = false;

  private reactToDrag(elem: HTMLElement) {
    if (elem.getAttribute('data-type') === 'DragHandle') {
      this.canDrag = true;
    }
  }

  private findDropTarget(elem: HTMLElement): HTMLElement {
    while (elem.getAttribute('data-type') !== 'DropTarget') {
      elem = elem.parentElement;
    }
    return elem;
  }

  private getMap(fieldName: string) {
    const map = {};
    this.props.settings.managedFields[fieldName].forEach((name) => {
      // special handling for vat field
      map[name] = (fieldName !== 'VAT') ? name : convertVatpctFromLocale(name, this.props.locale);
    });
    return map;
  }

  public render(): JSX.Element {
    const self = this;
    const actions: JSX.Element[] = [];

    if (this.props.onDelete && !this.props.readOnly) {
      actions.push((
        <span className="delete" key="onDelete" data-automation={this.props.automation + '-delete'}
          onClick={this.props.onDelete.bind(this)}></span>
      ));
    }

    const columns = [];
    for (const name of LineOrder) {
      if (this.props.columns.indexOf(name) < 0) {
        continue;
      }
      let res = null;
      switch (name) {
        case 'description':
          res = (<Input automation={this.props.automation + '-description'}
            onBlur={(description) => {
              self.props.onChange({ ...self.state, description });
            }}
            value={this.props.description}
            editable={true}
            borderless={false}
          />);
          break;
        case 'quantity':
          res = (
            <NumberInput formatter={this.numberF} automation={this.props.automation + '-quantity'}
              onBlur={(quantity) => {
                self.props.onChange({ ...self.state, quantity });
              }} value={this.props.quantity} decimals={2} minimum={0} align="left"
              editable={this.props.readOnly ? false : true}
              borderless={this.props.readOnly}
            />
          );
          break;
        case 'unitPrice':
          res = (
            <NumberInput formatter={this.currencyF} automation={this.props.automation + '-unitPrice'}
              onBlur={(unitPrice) => {
                self.props.onChange({ ...self.state, unitPrice });
              }} value={this.props.unitPrice} decimals={2} align="right"
              editable={this.props.readOnly ? false : true}
              borderless={this.props.readOnly}
            />
          );
          break;
        case 'vatPct':
          if (this.props.readOnly) {
            res = (
              <Input automation={this.props.automation + '-vatPct'}
                value={this.getMap('VAT')[this.state.vatPct]}
                editable={false}
                borderless={true}
              />
            );
          } else {
            res = (
              <SelectInput
                value={this.state.vatPct}
                automation={this.props.automation + '-vatPct'}
                searchable
                searchPlaceholder={l('search.Default')}
                map={this.getMap('VAT')}
                onChange={(value) => {
                  const vatPct = parseFloat(value);
                  self.props.onChange({ ...self.state, vatPct });
                }}
              />
            );
          }
          break;
        case 'netTotal':
          res = (
            <NumberInput formatter={this.currencyF} borderless={true} editable={false}
              automation={this.props.automation + '-net'} value={this.props.netTotal} decimals={2} />
          );
          break;
        case 'grossTotal':
          res = (
            <NumberInput formatter={this.currencyF} automation={this.props.automation + '-total'}
              value={this.props.grossTotal} decimals={2}
              editable={false}
              borderless={true}
            />
          );
          break;
        case 'unit':
          if (this.props.readOnly) {
            res = (
              <Input automation={this.props.automation + '-unit'}
                value={this.getMap('UNITS')[this.props.unit]}
                editable={false}
                borderless={true}
              />
            );
          } else {
            res = (
              <SelectInput
                value={this.props.unit}
                automation={this.props.automation + '-unit'}
                searchable
                searchPlaceholder={l('search.Default')}
                map={this.getMap('UNITS')}
                onChange={(unit) => {
                  self.props.onChange({ ...self.state, unit });
                }}
              />
            );
          }
          break;
        default:
          res = (
            <span></span>
          );
      }
      if (res) {
        columns.push((
          <td key={name} className={`invoiceLine-${name}`}>{res}</td>
        ));
      }
    }

    return (
      <tr className="Controlled InvoiceLine" data-type="DropTarget"
        draggable={!this.props.readOnly && this.state.dragOn}
        onDragOver={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement);
          elem.classList.add('Dropping');
          e.preventDefault();
        }} onDragLeave={(e) => {
          const elem = self.findDropTarget(e.target as HTMLElement);
          elem.classList.remove('Dropping');
          e.preventDefault();
        }} onDrop={(e) => {
          const elem = self.findDropTarget((e.target as HTMLElement));
          elem.classList.remove('Dropping');
          self.props.onDrop(parseInt(e.dataTransfer.getData('id'), 10));
          e.preventDefault();
        }} onDragStart={(e) => {
          if (!self.canDrag) {
            e.preventDefault();
            return false;
          }
          e.dataTransfer.setData('id', '' + self.props.id);
        }}
        onDragEnd={(e) => {
          self.canDrag = false;
        }}
        onMouseDown={(e) => {
          self.reactToDrag((e.target as HTMLElement));
        }} data-automation={this.props.automation}
      >
        <td data-automation={this.props.automation + '-dragHandle'}
          data-type="DragHandle"
          className="dragHandle"
          // @ts-ignore
          onMouseEnter={() => document.activeElement.blur()}
          onMouseOver={() => self.setState({ dragOn: true })}
          onMouseLeave={() => self.setState({ dragOn: false })}
        >
        </td>
        {columns}
        <td>{actions}</td>
      </tr>
    );
  }
}
